// Shared between WhatsApp.jsx and Settings.jsx's Système section. Kept in
// lib/ to avoid a circular import: WhatsApp.jsx already imports
// SectionHeader/LoadError from Settings.jsx.

// "au total": queue_health() (backend/notifications/worker.py) counts
// SENT/FAILED with no date filter (all-time), unlike oldest_pending/
// recent_failures_1h which are live/windowed. Labeled honestly instead of
// reading as a live activity count.
export const QUEUE_STATUS_LABELS = {
  PENDING: "En attente",
  PROCESSING: "En cours",
  SENT: "Envoyés au total",
  FAILED: "Échecs au total",
};

// Same shape read from both /whatsapp/status and /settings/system's
// `whatsapp` field ({enabled, connected?, error?}): four distinct states,
// "Désactivé" is never conflated with "Déconnecté"/"Indisponible".
export function whatsappStatusLabel(status) {
  if (!status?.enabled) return { label: "Désactivé", dot: "bg-slate-300" };
  if (status.error) return { label: "Indisponible", dot: "bg-red-500" };
  if (isWhatsAppLinked(status)) return { label: "Connecté", dot: "bg-emerald-500" };
  return { label: "Déconnecté", dot: "bg-amber-500" };
}

// A WhatsApp account is linked when GOWA reports it logged in: `connected`
// alone only means GOWA reaches WhatsApp's servers, which is also true while
// a QR code is waiting to be scanned. Without `logged_in` (older answers),
// this falls back on `connected`.
export function isWhatsAppLinked(status) {
  if (!status || status.error) return false;
  return typeof status.logged_in === "boolean" ? status.logged_in : !!status.connected;
}

// Team alerts sent to a WhatsApp group: where each kind goes. The modes mirror the server's closed list
// (settings/models.py ASSIST_DELIVERY_MODES / TICKETS_DELIVERY_MODES); the keys are « <kind>.delivery », « <kind>.group_jid »,
// « <kind>.group_name » in the notifications section.
const deliveryOptions = (people) => [
  { value: "individual", label: `À chaque ${people.one}`, hint: "Chacun reçoit l'alerte sur son WhatsApp." },
  { value: "group", label: "Dans un groupe WhatsApp", hint: "Un seul message dans le groupe. S'il ne part pas, chacun est prévenu individuellement." },
  { value: "both", label: "Les deux", hint: `Un message dans le groupe et une alerte à chaque ${people.one}.` },
];

export const ALERT_DELIVERIES = {
  assist: {
    title: "Demandes d'assistance : où envoyer l'alerte ?",
    hint: "S'applique à la nouvelle demande et à son rappel 5 minutes plus tard.",
    options: deliveryOptions({ one: "technicien" }),
    missing: "Choisissez le groupe qui recevra les alertes d'assistance.",
  },
  tickets: {
    title: "Tickets support : où envoyer l'alerte d'équipe ?",
    hint: "S'applique à une demande venue de Jamora et à un ticket qui devient critique. Le responsable d'un ticket est toujours prévenu personnellement.",
    options: deliveryOptions({ one: "gestionnaire des tickets" }),
    missing: "Choisissez le groupe qui recevra les alertes des tickets.",
  },
  weekly: {
    title: "Résumé hebdomadaire : qui le reçoit ?",
    hint: "Désactivé tant que vous ne choisissez pas. Chaque personne reçoit SON résumé (calculé avec ses droits) seulement si elle l'a activé dans son profil ; le groupe reçoit celui de l'équipe, en chiffres, sans nom ni montant.",
    options: [
      { value: "none", label: "Désactivé", hint: "Aucun résumé n'est préparé ni envoyé." },
      { value: "individual", label: "Chaque personne qui l'a demandé", hint: "Sur son WhatsApp et dans sa cloche, selon ses choix de notification." },
      { value: "group", label: "Dans un groupe WhatsApp seulement", hint: "Un seul message, le résumé de l'équipe." },
      { value: "both", label: "Les deux", hint: "Les résumés personnels et celui de l'équipe dans le groupe." },
    ],
    missing: "Choisissez le groupe qui recevra le résumé de l'équipe.",
    groupNote: "Le message ne contient que des chiffres de l'équipe, jamais de nom ni de montant.",
  },
};

export const ALERT_DELIVERY_KINDS = Object.keys(ALERT_DELIVERIES);

export const deliveryFromApiSettings = (settings, kind) => ({
  delivery: settings[`${kind}.delivery`] || (kind === "weekly" ? "none" : "individual"),
  group_jid: settings[`${kind}.group_jid`] || "",
  group_name: settings[`${kind}.group_name`] || "",
});

export const deliveryEqual = (a, b) => a.delivery === b.delivery && a.group_jid === b.group_jid && a.group_name === b.group_name;

// A group mode without a chosen group cannot be saved (the server refuses it too).
export function deliveryMissingGroup({ delivery, group_jid: groupJid }) {
  return (delivery === "group" || delivery === "both") && !groupJid;
}

export const WEEKLY_DAYS = [
  ["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"], ["thursday", "Jeudi"], ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"],
];
export const weeklyScheduleFromApiSettings = (settings) => ({ day: settings["weekly.day"] || "monday", hour: Number.isFinite(Number(settings["weekly.hour"])) ? Number(settings["weekly.hour"]) : 7 });
export const weeklyScheduleEqual = (a, b) => a.day === b.day && Number(a.hour) === Number(b.hour);

// The last message sent to the group, in plain words (null when none was ever sent).
export function lastGroupMessageText(message, formatDate) {
  if (!message) return null;
  const at = formatDate(message.sent_at || message.created_at);
  if (message.status === "SENT") return { tone: "ok", text: `Dernier message dans le groupe envoyé le ${at}.` };
  if (message.status === "FAILED") {
    return { tone: "error", text: `Le dernier message dans le groupe n'est pas parti (${at})${message.fallback_sent_at ? " : chacun a été prévenu individuellement" : ""}. Vérifiez que le compte WhatsApp du CRM fait toujours partie du groupe.` };
  }
  return { tone: "pending", text: `Un message pour le groupe est en cours d'envoi (${at}).` };
}
